import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Typography } from '@mui/material'
import useShopSmartMeter from 'src/Libs/shop/hooks/use-shop-smart-meter'
import { extractSmartMeterDate, getSmartMeterIdDocumentsFromCart } from 'src/Libs/shop/utils/smart-meter'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { makeStyles } from 'tss-react/mui'
import { ShopCheckoutSideBarResponse } from 'src/Libs/shop/models'
import { SmartMeterDocument } from 'src/Libs/shop/models/system-configure-checkout-overview.model'
import { SmartMeterFormValues } from './SmartMeterForm'
import EditExistingSmartMeterAccordion from './EditExistingSmartMeterAccordion'

interface EditExistingSmartMeterProps {
  smartMeterCartId: string
  uploadedFiles: SmartMeterDocument[] | null
  onCartResponse?: (cartData: ShopCheckoutSideBarResponse | null) => void
  handleUploadedFiles: (uploadedFiles: SmartMeterDocument[] | null) => void
}

const useStyles = makeStyles()(theme => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    maxHeight: '60vh',
    overflow: 'auto',
    padding: theme.spacing(2),
  },
}))

/*
 * This component renders the existing smartmeter contracts.
 */
const EditExistingSmartMeter: React.FC<EditExistingSmartMeterProps> = ({ smartMeterCartId, uploadedFiles, handleUploadedFiles, onCartResponse }) => {
  const { editSmartMeterContract, removeSmartMeterContract, openSmartMeterContract, isLoading } = useShopSmartMeter()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const existingFilesLength = uploadedFiles ? Object.keys(uploadedFiles).length : 0

  const handleFileDelete = (smartMeterId: string, contractId: string): void => {
    removeSmartMeterContract(smartMeterId, contractId)
      .then(result => {
        if (!result) {
          return enqueueErrorStack(t('shop.smart-meter-document.delete-failed'))
        }

        const smartMeterCartDocuments = getSmartMeterIdDocumentsFromCart(result.lineItems, smartMeterId)
        handleUploadedFiles(smartMeterCartDocuments?.smartMeterDocuments ?? null)
        enqueueSuccessStack(t('shop.smart-meter-document.deleted-sucessfully'))
        if (onCartResponse) {
          onCartResponse(result)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleFileLoad = (smartMeterId: string, contractId: string): void => {
    openSmartMeterContract(smartMeterId, contractId)
      .then(result => {
        if (!result) {
          return enqueueErrorStack(t('shop.smart-meter-document.load-failed'))
        }
        const pdfBlob = new Blob([result], { type: 'application/pdf' })
        const pdfUrl = URL.createObjectURL(pdfBlob)
        window.open(pdfUrl, '_blank')
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleFileModify = (smartMeterId: string, contractId: string, contractFile: Blob | null, contractData: SmartMeterFormValues): void => {
    editSmartMeterContract(smartMeterId, contractId, contractFile, contractData)
      .then(result => {
        if (!result) {
          return enqueueErrorStack(t('shop.smart-meter-document.delete-failed'))
        }
        const smartMeterCartDocuments = getSmartMeterIdDocumentsFromCart(result.lineItems, smartMeterId)
        handleUploadedFiles(smartMeterCartDocuments?.smartMeterDocuments ?? null)
        enqueueSuccessStack(t('shop.smart-meter-document.modified-sucessfully'))
        if (onCartResponse) {
          onCartResponse(result)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  return (
    <Box className={classes.container}>
      <Typography align="center" variant="body2" color="textSecondary">
        {existingFilesLength > 0 ? t('shop.smart-meter.selected-file-label') : t('shop.smart-meter.no-selected-file-label')}
      </Typography>
      {uploadedFiles && (
        <>
          {Object.values(uploadedFiles).map(contract => {
            const targetInstallationDate = extractSmartMeterDate(contract.targetInstallationDate)
            const initialValues: SmartMeterFormValues = {
              firstName: contract.firstName,
              lastName: contract.lastName,
              street: contract.street,
              streetNumber: contract.streetNumber,
              zipCode: contract.zipCode,
              city: contract.city,
              email: contract.email,
              mechanicName: contract.mechanicName,
              previousMeterNumber: contract.previousMeterNumber,
              targetInstallationDate: targetInstallationDate.date,
              administrationNumber: contract.administrationNumber,
              targetInstallationTime: targetInstallationDate.time,
              confirmCustomerData: true,
            }

            return (
              <EditExistingSmartMeterAccordion
                key={contract.id}
                documentId={contract.id}
                existingfileName={contract.fileName}
                initialValues={initialValues}
                smartMeterCartId={smartMeterCartId}
                isLoading={isLoading}
                onOpenFile={handleFileLoad}
                onDeleteFile={handleFileDelete}
                onModifyFile={handleFileModify}
              />
            )
          })}
        </>
      )}
    </Box>
  )
}

export default EditExistingSmartMeter
